import React from "react";
import { cn } from "@/lib/utils";

/**
 * ErrorState — surface-level failure primitive.
 *
 * Differs from Alert: this is a full-panel replacement for content
 * that failed to load (e.g. a table that hit a 500). Alert is for
 * inline notices.
 *
 * Props:
 *   • title       — required (default: "Something went wrong")
 *   • description — optional secondary paragraph
 *   • retry       — optional callback; renders a "Try again" button
 *   • action      — optional custom action slot (renders NEXT to retry)
 *   • details     — optional string (e.g. correlation ID) rendered in
 *                   mono, muted, small print
 *
 * A11y: `role="alert"` so the failure is announced when it mounts.
 */

export function ErrorState({
  title = "Something went wrong",
  description,
  retry,
  action,
  details,
  className,
  "data-testid": testId,
}) {
  return (
    <div
      role="alert"
      data-testid={testId}
      className={cn(
        "flex flex-col items-center justify-center text-center",
        "rounded-[var(--ds-radius-lg)] border border-[var(--ds-color-danger)]",
        "bg-[var(--ds-color-danger-soft)] px-6 py-8",
        className
      )}
    >
      <h3 className="text-[16px] font-semibold text-[var(--ds-color-danger)]">
        {title}
      </h3>
      {description && (
        <p className="mt-1 max-w-md text-[13px] leading-snug text-[var(--ds-color-ink)]">
          {description}
        </p>
      )}
      {details && (
        <code
          className={cn(
            "mt-3 rounded-[var(--ds-radius-sm)] bg-[var(--ds-color-surface)] px-2 py-1",
            "font-[var(--ds-font-mono)] text-[11px] text-[var(--ds-color-ink-muted)]"
          )}
        >
          {details}
        </code>
      )}
      {(retry || action) && (
        <div className="mt-4 flex items-center gap-2">
          {retry && (
            <button
              type="button"
              onClick={retry}
              data-testid={testId ? `${testId}-retry` : undefined}
              className={cn(
                "inline-flex items-center gap-2 h-9 px-3 rounded-[var(--ds-radius-md)]",
                "bg-[var(--ds-color-surface)] border border-[var(--ds-color-border-strong)]",
                "text-[13px] font-medium text-[var(--ds-color-ink)]",
                "hover:bg-[var(--ds-color-surface-muted)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)]"
              )}
            >
              Try again
            </button>
          )}
          {action}
        </div>
      )}
    </div>
  );
}

export default ErrorState;
